import React, { useState, useEffect, useCallback } from 'react';
import { staffApi } from '../staffApi';
import { ACTIVE_CHAT_POLLING_INTERVAL, MESSAGES_PER_PAGE } from '../../../constants';
import ChatList from './components/ChatList';
import ChatMessages from './components/ChatMessages';
import CreateChatRoom from './components/CreateChatRoom';

function Inbox() {
    const [selectedChat, setSelectedChat] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [chatrooms, setChatrooms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [messageError, setMessageError] = useState(null);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [mobileView, setMobileView] = useState('list');
    const [showCreateChatRoom, setShowCreateChatRoom] = useState(false);
    
    // Pagination states
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [messagesOffset, setMessagesOffset] = useState(0);
    
    // Smart polling states
    const [lastChatroomCount, setLastChatroomCount] = useState(0);
    const [lastMessageCount, setLastMessageCount] = useState(0);
    const [hasNewChats, setHasNewChats] = useState(false);
    const [hasNewMessages, setHasNewMessages] = useState(false);
    const [showRefreshNotification, setShowRefreshNotification] = useState(false);

    useEffect(() => {
        return () => {
            if (window.chatListInterval) clearInterval(window.chatListInterval);
            if (window.messageInterval) clearInterval(window.messageInterval);
        };
    }, []);

    const fetchMessages = useCallback(async (chatroomId, chatName, isInitialLoad = false, offset = 0, append = false) => {
        if (!chatroomId) return;
        
        try {
            if (isInitialLoad) {
                setLoadingMessages(true);
                setMessagesOffset(0);
            } else if (append) {
                setLoadingMoreMessages(true);
            }
            
            // Ensure offset is a number
            const numericOffset = Number(offset);
            console.log(`[Inbox] Fetching messages - chatroomId=${chatroomId}, offset=${numericOffset} (type: ${typeof numericOffset}), append=${append}, isInitialLoad=${isInitialLoad}`);
            const response = await staffApi.getChatroomMessages(chatroomId, MESSAGES_PER_PAGE, numericOffset);
            console.log(`[Inbox] Received response - messages count: ${response?.data?.messages?.length || 0}, pagination:`, response?.data?.pagination);
            
            // Handle new response structure with pagination
            const messagesData = response?.data?.messages || response?.data || [];
            const pagination = response?.data?.pagination || {};
            
            if (!messagesData || messagesData.length === 0) {
                if (isInitialLoad) {
                    setMessages([]);
                    setHasMoreMessages(false);
                }
                setMessageError('');
                return;
            }
            
            const formattedMessages = messagesData.map(message => ({
                id: message.id,
                sender: message.sender_role || 'unknown',
                senderName: message.sender_name,
                content: message.content,
                timestamp: message.timestamp,
                isRead: message.is_read,
                readAt: message.read_at,
                senderTimezone: message.sender_timezone || null
            }));
            
            // Check for new messages (only for polling, not for pagination)
            if (!isInitialLoad && !append && formattedMessages.length > lastMessageCount) {
                setHasNewMessages(true);
                setShowRefreshNotification(true);
            }
            
            // Update messages - append if loading more, replace if initial load
            if (append) {
                // Prepend older messages to the beginning, filtering out duplicates
                setMessages(prevMessages => {
                    const existingIds = new Set(prevMessages.map(m => m.id));
                    const newMessages = formattedMessages.filter(m => !existingIds.has(m.id));
                    console.log(`[Inbox] Appending ${newMessages.length} new messages (filtered ${formattedMessages.length - newMessages.length} duplicates)`);
                    return [...newMessages, ...prevMessages];
                });
                // Update offset to the next position (current offset + number of messages loaded)
                setMessagesOffset(prevOffset => {
                    const newOffset = prevOffset + formattedMessages.length;
                    console.log(`[Inbox] Updating offset: ${prevOffset} + ${formattedMessages.length} = ${newOffset}`);
                    return newOffset;
                });
                setHasMoreMessages(pagination.has_more || false);
            } else if (isInitialLoad || !hasNewMessages) {
                // Replace messages on initial load or refresh
                setMessages(formattedMessages);
                // Set offset to the number of messages we just loaded
                const newOffset = formattedMessages.length;
                console.log(`[Inbox] Initial load, setting offset to ${newOffset}`);
                setMessagesOffset(newOffset);
                setHasMoreMessages(pagination.has_more || false);
                setLastMessageCount(formattedMessages.length);
                setHasNewMessages(false);
            }
            
            setMessageError('');
        } catch (err) {
            console.error('Failed to fetch messages:', err);
            setMessageError('Failed to load messages');
            if (isInitialLoad) {
                setMessages([]);
            }
        } finally {
            if (isInitialLoad) {
                setLoadingMessages(false);
            }
            if (append) {
                setLoadingMoreMessages(false);
            }
        }
    }, [lastMessageCount, hasNewMessages]);

    const fetchChatrooms = useCallback(async (isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setLoading(true);
            }
            const response = await staffApi.getChatrooms();
            const formattedChats = (response.results || []).map(chat => ({
                id: chat.id,
                name: chat.other_user_name,
                lastMessage: chat.last_message ? chat.last_message.content : 'No messages yet',
                unread: chat.unread_count,
                role: 'tutor',
                avatar: chat.other_user_name ? chat.other_user_name.charAt(0).toUpperCase() : 'T',
                avatarColor: '#10B981', // Green color for tutors
                isOnline: true, // Assume tutors are online
                subject: chat.prospect_details?.subject,
                grade: chat.prospect_details?.grade,
                notes: chat.prospect_details?.notes,
                demoDate: chat.prospect_details?.demo_date,
                demoTime: chat.prospect_details?.demo_time,
                timezone: chat.prospect_details?.timezone,
                dateTime: new Date(chat.created_at).toLocaleDateString(),
                leads: chat.leads || null
            }));
            
            // Check for new chatrooms
            if (!isInitialLoad && formattedChats.length > lastChatroomCount) {
                setHasNewChats(true);
                setShowRefreshNotification(true);
            }
            
            // Only update state on initial load or when user manually refreshes
            if (isInitialLoad || !hasNewChats) {
                setChatrooms(formattedChats);
                setLastChatroomCount(formattedChats.length);
                setHasNewChats(false);
                
                if (selectedChat) {
                    const updatedSelectedChat = formattedChats.find(chat => chat.id === selectedChat.id);
                    if (updatedSelectedChat) {
                        setSelectedChat(updatedSelectedChat);
                    }
                } else if (formattedChats.length > 0 && !selectedChat) {
                    setSelectedChat(formattedChats[0]);
                }
            }
        } catch (err) {
            console.error('Failed to fetch chatrooms:', err);
            setError('Failed to load conversations');
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            }
        }
    }, [selectedChat, lastChatroomCount, hasNewChats]); 

    // Fetch chatrooms on component mount
    useEffect(() => {
        fetchChatrooms(true);
    }, []);

    // Fetch messages when selected chat changes
    useEffect(() => {
        if (!selectedChat) return;

        // Initial load of last messages (based on MESSAGES_PER_PAGE)
        fetchMessages(selectedChat.id, selectedChat.name, true, 0, false);

        // Poll for unread messages only
        const pollInterval = setInterval(async () => {
            try {
                const response = await staffApi.getUnreadMessages(selectedChat.id);
                if (response.data && response.data.length > 0) {
                    // Only re-fetch last messages if there are new unread messages
                    await fetchMessages(selectedChat.id, selectedChat.name, false, 0, false);
                }
            } catch (error) {
                console.error('Error polling for unread messages:', error);
            }
        }, ACTIVE_CHAT_POLLING_INTERVAL);

        return () => clearInterval(pollInterval);
    }, [selectedChat, fetchMessages]);
    
    // Handler to load more messages
    const handleLoadMoreMessages = useCallback(() => {
        if (!selectedChat || loadingMoreMessages || !hasMoreMessages) return;
        
        console.log(`[Inbox] Load more clicked, current messagesOffset=${messagesOffset}`);
        // Use the current offset (which represents how many messages we've already loaded)
        // This will fetch the next batch of older messages
        fetchMessages(selectedChat.id, selectedChat.name, false, messagesOffset, true);
    }, [selectedChat, messagesOffset, loadingMoreMessages, hasMoreMessages, fetchMessages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedChat) return;
        
        try {
            setSendingMessage(true);
            await staffApi.sendMessage(selectedChat.id, newMessage.trim());
            setNewMessage('');
            // Refresh to show the latest messages including the new one
            await fetchMessages(selectedChat.id, selectedChat.name, false, 0, false);
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setSendingMessage(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    const handleMessageChange = (e) => {
        setNewMessage(e.target.value);
    };

    const handleChatRoomCreated = () => {
        // Refresh the chat list when a new chat room is created
        fetchChatrooms(true);
    };

    const handleRefreshChatrooms = () => {
        setHasNewChats(false);
        setShowRefreshNotification(false);
        fetchChatrooms(true);
    };

    const handleRefreshMessages = () => {
        if (!selectedChat) return;
        setHasNewMessages(false);
        setShowRefreshNotification(false);
        fetchMessages(selectedChat.id, selectedChat.name, true, 0, false);
    };

    const handleDismissNotification = () => {
        setShowRefreshNotification(false);
    };

    useEffect(() => {
        if (selectedChat) {
            setMobileView('messages');
        } else {
            setMobileView('list');
        }
    }, [selectedChat]);

    // Set default selected chat to the first chatroom when chatrooms are loaded
    useEffect(() => {
        if (!selectedChat && chatrooms.length > 0) {
            setSelectedChat(chatrooms[0]);
        }
    }, [chatrooms, selectedChat]);

    return (
        <div className="h-screen bg-gray-50 p-4">
            {/* Refresh Notification */}
            {showRefreshNotification && (
                <div className="fixed top-4 right-4 z-50 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5v-5a7.5 7.5 0 1 0-7.5 7.5" />
                        </svg>
                        <span className="text-sm font-medium">
                            {hasNewChats && hasNewMessages ? 'New chats and messages available' :
                             hasNewChats ? 'New chats available' :
                             hasNewMessages ? 'New messages available' : 'Updates available'}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={hasNewChats ? handleRefreshChatrooms : handleRefreshMessages}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium transition-colors"
                        >
                            Refresh
                        </button>
                        <button
                            onClick={handleDismissNotification}
                            className="px-3 py-1 bg-blue-400 hover:bg-blue-500 rounded text-sm font-medium transition-colors"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            <div className="h-[calc(100vh-2rem)] flex bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Left Panel - Chat List */}
                <div className={`w-80 bg-white flex flex-col h-full ${mobileView === 'list' ? 'block' : 'hidden md:flex'}`}>
                    <ChatList 
                        chatrooms={chatrooms}
                        selectedChat={selectedChat}
                        onChatSelect={(chat) => setSelectedChat(chat)}
                        loading={loading}
                        error={error}
                        onCreateChatRoom={() => setShowCreateChatRoom(true)}
                        onRefresh={handleRefreshChatrooms}
                        hasNewChats={hasNewChats}
                    />
                </div>

                {/* Middle Panel - Chat Messages */}
                <div className={`flex-1 bg-white flex flex-col h-full ${mobileView === 'messages' ? 'block' : 'hidden md:flex'}`}>
                    {/* Mobile Header */}
                    <div className="md:hidden flex items-center gap-2 p-3 border-b border-gray-200 bg-white">
                        {selectedChat && (
                            <button 
                                onClick={() => setMobileView('list')}
                                className="text-blue-600 font-semibold px-3 py-2"
                            >
                                ← Back
                            </button>
                        )}
                        {selectedChat && (
                            <div className="flex-1 font-semibold">
                                <span>{selectedChat.name}</span>
                            </div>
                        )}
                    </div>

                            <ChatMessages 
                                selectedChat={selectedChat}
                                messages={messages}
                                loadingMessages={loadingMessages}
                                messageError={messageError}
                                newMessage={newMessage}
                                sendingMessage={sendingMessage}
                                onMessageChange={handleMessageChange}
                                onKeyDown={handleKeyDown}
                                onSendMessage={handleSendMessage}
                                onRefresh={handleRefreshMessages}
                                hasNewMessages={hasNewMessages}
                                hasMoreMessages={hasMoreMessages}
                                loadingMoreMessages={loadingMoreMessages}
                                onLoadMore={handleLoadMoreMessages}
                            />
                </div>
            </div>

            {/* Create Chat Room Modal */}
            <CreateChatRoom
                isOpen={showCreateChatRoom}
                onClose={() => setShowCreateChatRoom(false)}
                onChatRoomCreated={handleChatRoomCreated}
            />
        </div>
    );
}

export default Inbox;